import React from "react";

// ---------- GanttBarLabel ----------
// Feb 2026 · Shared label renderer for Gantt bars. Extracted so the phased
// and no-phased render branches in GanttTab.jsx stop drifting apart —
// iterations 55, 56, 57 and 60 all had to apply the same fix in two
// places. Now there's one source of truth.
//
// Renders `<WO-No> / <product-code> · <SO-No>` (or `<SO-No> / <code>` when
// no WO is linked) plus the schedule-status chips: emerald `ON TRACK`
// when the plan finishes ≤ line delivery, red `+Nd late` when past.

export function GanttBarLabel({
  plan, woNo, orderNo, productCode, progress,
  onTrackVsDelivery, atRisk, isOverdue,
  promisedDelivery, daysOverdueVsDelivery,
  isShard, includeProgress,
}) {
  // Feb 2026 · V2 label — prefer the healed `planned_label` field the
  // startup migration stamped onto every plan. When the plan carries
  // its own composite string we render it verbatim so the label survives
  // even after the source SO/WO is renamed or cancelled. The live
  // computed values still act as fallback for freshly-created plans
  // that haven't been re-fetched yet.
  const healed = plan && typeof plan.planned_label === "string" && plan.planned_label.trim();
  const lead = woNo || orderNo || "—";
  return (
    <>
      <span
        className={isShard ? "text-[8px] whitespace-nowrap" : ""}
        style={isShard ? { lineHeight: 1 } : undefined}
        data-testid={`gantt-bar-label-${plan.id}`}
      >
        {healed ? (
          <span className="text-white/95" title={healed}>{healed}</span>
        ) : (
          <>
            <span className="text-white/95">{lead}</span>
            {productCode && <span className="opacity-70"> / {productCode}</span>}
            {woNo && orderNo && <span className="opacity-60"> · {orderNo}</span>}
          </>
        )}
      </span>
      {includeProgress && progress > 0 && (
        <span className="opacity-80">· {progress.toFixed(0)}%</span>
      )}
      {atRisk && !isOverdue && (
        <span
          data-testid={`gantt-days-overdue-${plan.id}`}
          className="text-[9px] font-bold px-1 ml-1 rounded-sm bg-red-500/70 border border-red-300/60 text-white uppercase tracking-widest flex-shrink-0"
          title={`Planned finish ${plan.planned_finish} exceeds weaving deadline ${promisedDelivery} by ${daysOverdueVsDelivery}d · 4-day post-loom buffer`}
        >+{daysOverdueVsDelivery}d late</span>
      )}
      {onTrackVsDelivery && (
        <span
          data-testid={`gantt-on-track-${plan.id}`}
          className="text-[9px] font-bold px-1 ml-1 rounded-sm bg-emerald-500/25 border border-emerald-400/40 text-emerald-200 uppercase tracking-widest flex-shrink-0"
          title={`On track — finishes ${plan.planned_finish} ≤ weaving deadline ${promisedDelivery} (delivery − 4d buffer)`}
        >ON TRACK</span>
      )}
    </>
  );
}
